/**
 * React Query хуки ресурса «Аномалии v3» (L3).
 *
 * Ключи namespaced отдельно от anomaliesKeys (['analytics-anomalies']) и от
 * analyticsKeys (['analytics']): у v3 другое зерно (кейс+сценарий), инвалидации
 * legacy-слоёв не должны его задевать и наоборот.
 */

import { useQuery } from '@tanstack/react-query'
import * as api from './anomalies-v3.api'
import type { CasesV3Params } from './anomalies-v3.api'

export const anomaliesV3Keys = {
  all: ['analytics-anomalies-v3'] as const,
  summary: (p: CasesV3Params) => [...anomaliesV3Keys.all, 'summary', p] as const,
  list: (p: CasesV3Params) => [...anomaliesV3Keys.all, 'list', p] as const,
  detail: (caseId: string, scenarioId: number) =>
    [...anomaliesV3Keys.all, 'detail', caseId, scenarioId] as const,
  similar: (caseId: string, scenarioId: number, page: number) =>
    [...anomaliesV3Keys.all, 'similar', caseId, scenarioId, page] as const,
  evidenceExamples: (caseId: string, scenarioId: number, evidenceKey: string) =>
    [...anomaliesV3Keys.all, 'evidence-examples', caseId, scenarioId, evidenceKey] as const,
  /**
   * Ключ подробностей — ТОЧНАЯ идентичность обеих ног, и ничего кроме неё.
   *
   * Ни кейс, ни сценарий, ни блок доказательств в ключ не входят: подробности одной
   * и той же перевозки одинаковы, откуда бы её ни открыли, — поэтому открытая из
   * «истории вагона» строка не запрашивается заново в окне 30 дней.
   */
  historyDetail: (importRecordId: string, exportRecordId: string) =>
    [...anomaliesV3Keys.all, 'history-detail', importRecordId, exportRecordId] as const,
}

export function useAnomalyCasesSummaryV3(p: CasesV3Params = {}) {
  return useQuery({
    queryKey: anomaliesV3Keys.summary(p),
    queryFn: () => api.getAnomalyCasesSummaryV3(p),
    staleTime: 5 * 60_000,
  })
}

export function useAnomalyCasesListV3(p: CasesV3Params) {
  return useQuery({
    queryKey: anomaliesV3Keys.list(p),
    queryFn: () => api.getAnomalyCasesListV3(p),
    staleTime: 60_000,
  })
}

export function useAnomalyCaseDetailV3(caseId: string | null, scenarioId: number | null) {
  return useQuery({
    queryKey: anomaliesV3Keys.detail(caseId ?? '', scenarioId ?? -1),
    queryFn: () => api.getAnomalyCaseDetailV3(caseId!, scenarioId!),
    enabled: !!caseId && scenarioId != null,
    staleTime: 5 * 60_000,
  })
}

/**
 * Lazy: строки за одним числом доказательств уходят в сеть только после клика.
 *
 * Кэш держится по (кейс, сценарий, показатель) — повторное раскрытие той же строки и
 * то же число, процитированное второй карточкой причины, не порождают новый запрос.
 * `signal` из React Query пробрасывается в fetch, поэтому закрытие drawer'а отменяет
 * незавершённый запрос.
 */
export function useCaseEvidenceExamplesV3(
  caseId: string | null,
  scenarioId: number | null,
  evidenceKey: string | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: anomaliesV3Keys.evidenceExamples(caseId ?? '', scenarioId ?? -1, evidenceKey ?? ''),
    queryFn: ({ signal }) => api.getCaseEvidenceExamplesV3(caseId!, scenarioId!, evidenceKey!, signal),
    enabled: enabled && !!caseId && scenarioId != null && !!evidenceKey,
    staleTime: 5 * 60_000,
    retry: 1,
  })
}

/**
 * Lazy: подробности исторической перевозки — ровно один запрос на открытие строки.
 *
 * На отрисовке таблицы не уходит ни одного запроса, сколько бы строк в ней ни было:
 * хук включается только когда пользователь открыл карточку. Повторное открытие той же
 * перевозки берётся из кэша.
 */
export function useCaseHistoryDetailV3(
  importRecordId: string | null,
  exportRecordId: string | null,
) {
  return useQuery({
    queryKey: anomaliesV3Keys.historyDetail(importRecordId ?? '', exportRecordId ?? ''),
    queryFn: ({ signal }) => api.getCaseHistoryDetailV3(importRecordId!, exportRecordId!, signal),
    enabled: !!importRecordId && !!exportRecordId,
    staleTime: 5 * 60_000,
    // «Не нашли» и «не однозначно» — это ответ, а не сбой: повторять их незачем.
    retry: 1,
  })
}

/** Lazy: запрос уходит только когда секция «Похожие кейсы» в drawer'е раскрыта. */
export function useSimilarCasesV3(
  caseId: string | null,
  scenarioId: number | null,
  page: number,
  enabled: boolean,
) {
  return useQuery({
    queryKey: anomaliesV3Keys.similar(caseId ?? '', scenarioId ?? -1, page),
    queryFn: () => api.getSimilarCasesV3(caseId!, scenarioId!, page),
    enabled: enabled && !!caseId && scenarioId != null,
    staleTime: 5 * 60_000,
  })
}
